import { List } from "immutable";
import State from "./model";
import Face from "./face";
import Die from "./die";
import { PIP_2, PIP_3, PIP_4, PIP_5, PIP_6, PIP_1, X, VP_1, VP_2, VP_3, VP_4 } from "./faces";
import { StateWithHistory } from "redux-undo";

export function getRolledFaces(state: { currentRoll: [ number, number, number, number, number ], dice: List<Die> }): List<Face> {
    if (state.currentRoll != undefined) {
        return List(state.currentRoll.map((faceIndex, dieIndex) => state.dice.get(dieIndex).faces.get(faceIndex)));
    }
    return List();
}

const allPermutations: List<List<number>> = List([
    List([0, 1, 2, 3, 4]),
    List([0, 1, 2, 3]),
    List([0, 1, 2, 4]),
    List([0, 1, 3, 4]),
    List([0, 2, 3, 4]),
    List([1, 2, 3, 4]),
    List([0, 1, 2]),
    List([0, 1, 3]),
    List([0, 1, 4]),
    List([0, 2, 3]),
    List([0, 2, 4]),
    List([0, 3, 4]),
    List([1, 2, 3]),
    List([1, 2, 4]),
    List([1, 3, 4]),
    List([2, 3, 4]),
    List([0, 1]),
    List([0, 2]),
    List([0, 3]),
    List([0, 4]),
    List([1, 2]),
    List([1, 3]),
    List([1, 4]),
    List([2, 3]),
    List([2, 4]),
    List([3, 4])
]);

function sum(collection: Iterable<number>): number {
    var sum = 0;
    for (const num of collection) {
        sum += num;
    }
    return sum;
}

export function getIncreasedValue(face: Face): Face {
    if (face.type == "Pips") {
        switch (face.value) {
            case 1: return PIP_2;
            case 2: return PIP_3;
            case 3: return PIP_4;
            case 4: return PIP_5;
            case 5: return PIP_6;
        }
    }
    return undefined;
}

export function getDecreasedValue(face: Face): Face {
    if (face.type == "Pips") {
        switch (face.value) {
            case 2: return PIP_1;
            case 3: return PIP_2;
            case 4: return PIP_3;
            case 5: return PIP_4;
            case 6: return PIP_5;
        }
    }
    return undefined;
}

function* allRollIndices(): IterableIterator<[number, number, number, number, number]> {
    for (let r0 = 0; r0 < 6; r0++) {
        for (let r1 = 0; r1 < 6; r1++) {
            for (let r2 = 0; r2 < 6; r2++) {
                for (let r3 = 0; r3 < 6; r3++) {
                    for (let r4 = 0; r4 < 6; r4++) {
                        yield [ r0, r1, r2, r3, r4 ];
                    }
                }
            }
        }
    }
}

export function oddsTable(dice: List<Die>): List<{ face: Face, odds: number}> {
    var counts = [ 0, 0, 0, 0, 0 ];
    for (let roll of allRollIndices()) {
        let sevens = availableSevens({ currentRoll: roll, dice: dice });
        if (sevens.count() == 0) {
            counts[0]++;
        }
        else {
            let bestScore = sevens.map(s => s.count() - 1).max();
            counts[bestScore]++;
        }
    }
    const totalCounts = Math.pow(6, 5);
    return List([
        { face: X, odds: 100 * counts[0] / totalCounts },
        { face: VP_1, odds: 100 * counts[1] / totalCounts },
        { face: VP_2, odds: 100 * counts[2] / totalCounts },
        { face: VP_3, odds: 100 * counts[3] / totalCounts },
        { face: VP_4, odds: 100 * counts[4] / totalCounts }        
    ]);
}

function isSumToSeven(diceIndices: List<number>, state: { currentRoll: [ number, number, number, number, number ], dice: List<Die> }): boolean {
    var rolledFaces = getRolledFaces(state);
    if (diceIndices.some(idx => rolledFaces.get(idx).type != "Pips")) {
        return false;
    }
    return sum(diceIndices.map(idx => (rolledFaces.get(idx) as any).value)) == 7;
}

export function availableSevens(state: { currentRoll: [ number, number, number, number, number ], dice: List<Die> }): List<List<number>> {
    if (state.currentRoll != undefined) {
        return allPermutations.filter(p => isSumToSeven(p, state)).toList();        
    }
    return undefined;
}

export function availableXLocations(state: { currentRoll: [ number, number, number, number, number ], dice: List<Die> }): List<number> {
    if (state.currentRoll != undefined) {
        if (availableSevens(state).count() == 0) {
            var rolledFaces = getRolledFaces(state);
            return List([0, 1, 2, 3, 4]).filter(idx => rolledFaces.get(idx).type != "X");
        }
    }
    return undefined;
}

export function score(state: { dice: List<Die> }): number {
    var score = 0;
    for (let die of state.dice) {
        for (let face of die.faces) {
            if (face.type == "Vp") {
                score += face.value;
            }
        }
    }
    return score;
}

export function strikes(state: { dice: List<Die> }): number {
    var strikes = 0;
    for (let die of state.dice) {
        for (let face of die.faces) {
            if (face.type == "X") {
                strikes += 1;
            }
        }
    }
    return strikes;
}

export function canUndo(state: StateWithHistory<State>): boolean {
    return state.past.length > 0 && state.present.status != "ROLLED";
}

export function canRedo(state: StateWithHistory<State>): boolean {
    return state.future.length > 0;
}